/**
 * Direct-stream decorator for a `PtyBridge`.
 *
 * Wraps the relay-backed bridge so terminal bytes reach viewers straight from
 * the pod (`PtyStreamServer`) instead of round-tripping through the API. The
 * relay is NOT removed — it stays as the fallback transport for viewers that
 * can't reach the tunnel (local dev with no preview-router, codespace-backed
 * workspaces, an older web client).
 *
 * The interesting behavior is what happens to the relay while a direct viewer
 * is attached: rather than going silent — which would freeze any fallback
 * viewer's screen — relay output is **coalesced to one frame every couple of
 * seconds**. That cuts the server's per-frame cost by ~50x while keeping the
 * relay ring live, and it keeps `bumpPtyCardActivity` firing (the server-side
 * bump is throttled to 30s anyway), so an agent working in the TUI still holds
 * its pod's activity clock open exactly as before.
 */

import type { PtyBridge } from "../types.js";
import { PtyOutputCoalescer } from "./output-coalescer.js";
import { PtyStreamServer } from "./stream-server.js";
import { createServiceLogger } from "../../utils/logger.js";

const logger = createServiceLogger("PtyDirectStream");

/** Relay flush interval while ≥1 direct viewer is attached. Slow enough to gut
 *  the per-frame server cost, fast enough that a fallback viewer sees a live
 *  (if less smooth) terminal rather than a frozen one. */
const RELAY_COALESCE_MS = 2_000;
/** Force a relay flush at this buffer size regardless of the timer, so a burst
 *  of output can't sit unrelayed (and stays under the 256KB frame cap). */
const RELAY_MAX_BUFFER_CHARS = 48 * 1024;

/** The connection surface the decorator needs — kept minimal so tests can pass
 *  a plain object and so this module never imports the connection layer. */
export interface PtyStreamReporter {
  readonly sessionId: string;
  reportPtyStream(port: number | null): void;
}

/** Test seam: pin the stream server's port window so a suite can't collide with
 *  a real agent running on the same host. Production passes nothing. */
export interface DirectPtyStreamOptions {
  portBase?: number;
  portAttempts?: number;
}

export interface DirectPtyStream {
  bridge: PtyBridge;
  /** Tear down the in-pod server and clear the reported port. Idempotent. */
  dispose: () => Promise<void>;
}

interface Dims {
  cols: number;
  rows: number;
}

/**
 * The decorator's mutable state. A class rather than one big closure so each
 * concern — server lifecycle, relay lane switching, dims reconciliation — stays
 * a small, separately readable method.
 */
class DirectStreamController {
  private server: PtyStreamServer | null = null;
  private starting = false;
  private disposed = false;

  // Handlers registered by the harness. The direct server feeds the SAME
  // handlers the relay does, so the pty sees one merged input/resize stream.
  private inputHandler: ((data: string) => void) | null = null;
  private resizeHandler: ((cols: number, rows: number) => void) | null = null;

  // Dims proposed by each transport. A PTY has one size, so the pty gets the
  // min box that fits both sets of viewers — the same rule each transport
  // already applies within itself.
  private relayDims: Dims | null = null;
  private directDims: Dims | null = null;

  private lastDims: Dims | undefined;
  private coalescing = false;
  private readonly relayCoalescer: PtyOutputCoalescer;

  constructor(
    private readonly inner: PtyBridge,
    private readonly reporter: PtyStreamReporter,
    private readonly options: DirectPtyStreamOptions,
  ) {
    // Dims are read from `lastDims` at flush time (rather than through the
    // coalescer's own getter) so an un-dimensioned frame relays with no dims at
    // all instead of a fabricated 0×0, which the relay's schema would reject.
    this.relayCoalescer = new PtyOutputCoalescer(
      (data) => this.inner.sendOutput(data, this.lastDims),
      () => this.lastDims ?? { cols: 0, rows: 0 },
      RELAY_COALESCE_MS,
      RELAY_MAX_BUFFER_CHARS,
    );
  }

  /** Start the in-pod server on first output. A session that never produces PTY
   *  output (SDK harness, a run that dies at spawn) never binds a port. */
  private ensureServer(): void {
    if (this.server || this.starting || this.disposed) return;
    this.starting = true;
    const created = new PtyStreamServer({
      sessionId: this.reporter.sessionId,
      onInput: (data) => this.inputHandler?.(data),
      onResize: (cols, rows) => {
        this.directDims = { cols, rows };
        this.applyDims();
      },
      ...this.options,
    });
    void created
      .listen()
      .then((port) => this.onListening(created, port))
      .catch((err: unknown) => {
        this.starting = false;
        logger.warn(
          `PTY stream server failed to start: ${err instanceof Error ? err.message : String(err)}`,
        );
      });
  }

  private onListening(created: PtyStreamServer, port: number | null): void {
    this.starting = false;
    // Disposed mid-bind, or the whole window was taken: stay on the relay.
    if (this.disposed || port === null) {
      void created.close();
      return;
    }
    this.server = created;
    this.reporter.reportPtyStream(port);
    logger.info(`PTY stream server listening on ${port} (session ${this.reporter.sessionId})`);
  }

  /** Push the min box across both transports to the pty. */
  private applyDims(): void {
    const candidates = [this.relayDims, this.directDims].filter((d): d is Dims => d !== null);
    if (candidates.length === 0 || !this.resizeHandler) return;
    this.resizeHandler(
      Math.min(...candidates.map((d) => d.cols)),
      Math.min(...candidates.map((d) => d.rows)),
    );
  }

  sendOutput(data: string, dims?: Dims): void {
    this.ensureServer();
    this.server?.broadcast(data, dims);
    if (dims) this.lastDims = dims;

    if ((this.server?.clientCount ?? 0) > 0) {
      this.coalescing = true;
      this.relayCoalescer.write(data);
      return;
    }
    // Back to the fast lane: flush whatever the slow lane still holds first so
    // relay watchers never see bytes out of order.
    if (this.coalescing) {
      this.coalescing = false;
      this.relayCoalescer.flush();
    }
    this.inner.sendOutput(data, dims);
  }

  sendEnded(): void {
    this.relayCoalescer.flush();
    this.server?.end();
    this.reporter.reportPtyStream(null);
    this.inner.sendEnded?.();
  }

  onInput(handler: (data: string) => void): () => void {
    this.inputHandler = handler;
    const off = this.inner.onInput(handler);
    return () => {
      if (this.inputHandler === handler) this.inputHandler = null;
      off();
    };
  }

  onResize(handler: (cols: number, rows: number) => void): () => void {
    this.resizeHandler = handler;
    const off = this.inner.onResize((cols, rows) => {
      this.relayDims = { cols, rows };
      this.applyDims();
    });
    return () => {
      if (this.resizeHandler === handler) this.resizeHandler = null;
      off();
    };
  }

  async dispose(): Promise<void> {
    if (this.disposed) return;
    this.disposed = true;
    this.relayCoalescer.dispose();
    this.reporter.reportPtyStream(null);
    const current = this.server;
    this.server = null;
    if (current) await current.close();
  }
}

/** Decorate `inner` with an in-pod stream server. */
export function wrapBridgeWithDirectStream(
  inner: PtyBridge,
  reporter: PtyStreamReporter,
  options: DirectPtyStreamOptions = {},
): DirectPtyStream {
  const controller = new DirectStreamController(inner, reporter, options);
  return {
    bridge: {
      ...inner,
      sendOutput: (data, dims) => controller.sendOutput(data, dims),
      sendEnded: () => controller.sendEnded(),
      onInput: (handler) => controller.onInput(handler),
      onResize: (handler) => controller.onResize(handler),
    },
    dispose: () => controller.dispose(),
  };
}
